import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import LogoUpload from '../../../components/LogoUpload';
import SyncableUrlInput from '../../../components/ui/SyncableUrlInput';
import { useUrlSync } from '../../../hooks/useUrlSync';
import { uploadAndSetLogo, deleteLogo } from '../../../utils/logoService';

const BusinessTab = ({ onMessage, onSave }) => {
  const { currentOrganization, updateOrganization } = useOrganization();
  const { syncToOrganization, PLATFORM_CONFIG } = useUrlSync();
  const [logoUploading, setLogoUploading] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Business settings state
  const [businessSettings, setBusinessSettings] = useState({
    business_name: '',
    timezone: 'America/New_York',
    google_review_url: '',
    yelp_url: '',
    facebook_url: ''
  });

  // Load current organization data
  useEffect(() => {
    if (currentOrganization && !hasUnsavedChanges) {
      setBusinessSettings({
        business_name: currentOrganization.business_name || currentOrganization.name || '',
        timezone: currentOrganization.timezone || 'America/New_York',
        google_review_url: currentOrganization.google_review_url || '',
        yelp_url: currentOrganization.yelp_url || '',
        facebook_url: currentOrganization.facebook_url || ''
      });
    }
  }, [currentOrganization, hasUnsavedChanges]);

  // Validate business settings
  const validateBusinessSetting = (key, value) => {
    const newErrors = { ...errors };
    
    if (key === 'business_name') {
      if (!value || value.trim().length === 0) {
        newErrors.business_name = 'Business name is required';
      } else if (value.trim().length < 2) {
        newErrors.business_name = 'Business name must be at least 2 characters';
      } else {
        delete newErrors.business_name;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save to database
  const saveBusinessSetting = async (key, value) => {
    // Validate before saving
    if (!validateBusinessSetting(key, value)) {
      return;
    }

    setSaving(true);
    
    try {
      const updateData = { [key]: value };
      
      // Map business_name to both name and business_name for consistency
      if (key === 'business_name') {
        const trimmedValue = value.trim();
        updateData.name = trimmedValue;
        updateData.business_name = trimmedValue;
      }
      
      const result = await updateOrganization(updateData);
      
      if (result.success) {
        if (onMessage) {
          onMessage('success', 'Business information updated successfully!');
        }
        // Clear any errors on successful save
        setErrors(prev => ({ ...prev, [key]: undefined }));
        
        // Mark changes as saved immediately for better UX
        setHasUnsavedChanges(false);
      } else {
        if (onMessage) {
          onMessage('error', result.error || 'Failed to save changes');
        }
      }
      
      // onSave callback removed - BusinessTab is now self-contained
    } catch (error) {
      if (onMessage) {
        onMessage('error', 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  // Cleanup timeout on unmount (for any remaining cleanup)
  const timeoutRef = useRef(null);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle immediate UI updates
  const handleBusinessSettingChange = (key, value) => {
    // Update UI immediately
    setBusinessSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Mark that we have unsaved changes for business name
    if (key === 'business_name') {
      setHasUnsavedChanges(true);
    }
    
    // Clear previous error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }

    
    // For URLs, still save immediately as they can be empty and are less critical
    if (key !== 'business_name') {
      saveBusinessSetting(key, value);
    }
  };

  // Manual save function for business name
  const handleSaveBusinessName = async () => {
    await saveBusinessSetting('business_name', businessSettings.business_name);
  };

  // Handle URL changes with sync capabilities
  const handleUrlChange = async (platform, value) => {
    const orgField = PLATFORM_CONFIG[platform]?.orgField;
    if (!orgField) return;

    await handleBusinessSettingChange(orgField, value);
  };

  // Handle sync to organization (this is already in organization, so just confirm)
  const handleSyncToOrg = async (platform, value) => {
    try {
      await handleUrlChange(platform, value);
      onMessage('success', `${PLATFORM_CONFIG[platform]?.label} URL updated successfully!`);
    } catch (error) {
      onMessage('error', `Failed to update ${PLATFORM_CONFIG[platform]?.label} URL`);
    }
  };

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;

    setLogoUploading(true);
    
    try {
      const result = await uploadAndSetLogo(selectedLogoFile, currentOrganization.id);
      
      if (result.success) {
        setSelectedLogoFile(null);
        onMessage('success', 'Logo uploaded successfully!');
        
        // Update organization with new logo URL
        await updateOrganization({ logo_url: result.logoUrl });
      } else {
        onMessage('error', result.error || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      onMessage('error', 'An unexpected error occurred while uploading the logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    
    try {
      const result = await deleteLogo(currentOrganization.logo_url);
      
      if (result.success) {
        onMessage('success', 'Logo removed successfully!');
        
        // Update organization to remove logo URL
        await updateOrganization({ logo_url: null });
      } else {
        onMessage('error', result.error || 'Failed to remove logo');
      }
    } catch (error) {
      console.error('Logo removal error:', error);
      onMessage('error', 'An unexpected error occurred while removing the logo');
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Organization Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Organization Information
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          This information appears in review forms and customer communications.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Business Name *
              {hasUnsavedChanges && !saving && (
                <span className="ml-2 text-sm text-amber-600">
                  <Icon name="AlertCircle" size={14} className="inline mr-1" />
                  Unsaved changes
                </span>
              )}
              {saving && (
                <span className="ml-2 text-sm text-blue-600">
                  <Icon name="Loader2" size={14} className="inline animate-spin mr-1" />
                  Saving...
                </span>
              )}
            </label>
            <div className="flex gap-3">
              <Input
                type="text"
                value={businessSettings.business_name}
                onChange={(e) => handleBusinessSettingChange('business_name', e.target.value)}
                placeholder="Your Business Name"
                error={errors.business_name}
                disabled={saving}
                className="flex-1"
              />
              <Button
                onClick={handleSaveBusinessName}
                disabled={saving || !hasUnsavedChanges || !businessSettings.business_name.trim()}
                className="px-4 py-2 bg-primary text-white hover:bg-primary-600 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-1" />
                    Saving
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} className="mr-1" />
                    Save
                  </>
                )}
              </Button>
            </div>
            {errors.business_name ? (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <Icon name="AlertCircle" size={14} className="mr-1" />
                {errors.business_name}
              </p>
            ) : (
              <p className="text-sm text-text-secondary mt-1">
                This name appears in review forms and customer communications
              </p>
            )}
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Timezone
            </label>
            <select
              value={businessSettings.timezone || 'America/New_York'}
              onChange={(e) => handleBusinessSettingChange('timezone', e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
              <option value="America/Phoenix">Arizona Time (MST)</option>
              <option value="America/Toronto">Eastern Time - Toronto</option>
              <option value="America/Vancouver">Pacific Time - Vancouver</option>
              <option value="Europe/London">London (GMT/BST)</option>
              <option value="Europe/Paris">Central European Time</option>
              <option value="Asia/Tokyo">Japan Standard Time</option>
              <option value="Australia/Sydney">Australian Eastern Time</option>
            </select>
            <p className="text-sm text-text-secondary mt-1">
              Used for scheduling review requests and automation timing
            </p>
          </div>

          {/* Logo Management Section */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Business Logo
            </label>
            <div className="space-y-4">
              <LogoUpload
                key={currentOrganization?.logo_url} // Force re-render when logo changes
                onFileSelect={setSelectedLogoFile}
                onFileRemove={() => setSelectedLogoFile(null)}
                initialLogoUrl={currentOrganization?.logo_url}
                disabled={logoUploading}
                showConstraints={true}
              />
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {selectedLogoFile && (
                  <Button
                    onClick={handleLogoUpload}
                    disabled={logoUploading}
                    className="bg-primary text-white hover:bg-primary-600"
                  >
                    {logoUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      'Upload Logo'
                    )}
                  </Button>
                )}
                
                {currentOrganization?.logo_url && !selectedLogoFile && (
                  <Button
                    onClick={handleLogoRemove}
                    disabled={logoUploading}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    {logoUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                        Removing...
                      </>
                    ) : (
                      'Remove Logo'
                    )}
                  </Button>
                )}
              </div>
              
              <p className="text-sm text-text-secondary">
                Your business logo will appear in the header next to ServiceFlow branding and can be used in review requests and other customer communications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Integration URLs */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Platform Integration
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Configure URLs for external review platforms. These serve as organization defaults and can be overridden in individual campaigns.
        </p>
        
        <div className="space-y-6">
          {/* Google Business Profile */}
          <SyncableUrlInput
            platform="google"
            value={businessSettings.google_review_url}
            onChange={(e) => handleUrlChange('google', e.target.value)}
            source="organization"
            showSyncButtons={false}
            placeholder={PLATFORM_CONFIG.google.placeholder}
          />
          
          {/* Yelp */}
          <SyncableUrlInput
            platform="yelp"
            value={businessSettings.yelp_url}
            onChange={(e) => handleUrlChange('yelp', e.target.value)}
            source="organization"
            showSyncButtons={false}
            placeholder={PLATFORM_CONFIG.yelp.placeholder}
          />
          
          {/* Facebook */}
          <SyncableUrlInput
            platform="facebook"
            value={businessSettings.facebook_url}
            onChange={(e) => handleUrlChange('facebook', e.target.value)}
            source="organization"
            showSyncButtons={false}
            placeholder={PLATFORM_CONFIG.facebook.placeholder}
          />
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Organization Defaults:</strong> These URLs are used as defaults across all campaigns. Individual campaigns can override these settings when needed.
            </div>
          </div>
        </div>
      </div>

      {/* AI Service Information */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Zap" size={20} className="text-green-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-green-900 mb-1">AI Review Enhancement</h4>
            <p className="text-green-800 text-sm mb-2">
              AI-powered review improvement is included with your ServiceFlow Pro subscription.
            </p>
            <ul className="text-green-800 text-sm space-y-1">
              <li>• Automatically suggests review improvements to customers</li>
              <li>• Uses OpenAI's GPT-3.5-turbo for natural language processing</li>
              <li>• Maintains customer's original sentiment and voice</li>
              <li>• No additional configuration required</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessTab; 